/**
 * Experience Map Utilities
 * Utilities for converting experiences to map markers
 */

import { ExtendedExperienceCard } from '@/constants/experiencesData';
import { MapMarker } from '@/components/common/map/types';
import { useCurrencyStore } from '@/store/currencyStore';
import { convertAmountWithRates } from '@/utils/currencyHelpers';

/**
 * Check if Google Maps is loaded
 */
function isGoogleMapsLoaded(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return !!window.google?.maps;
}

/**
 * Convert experiences to map markers
 * Uses geocoding service to get coordinates if not available in experience data
 */
export async function convertExperiencesToMarkers(
  experiences: ExtendedExperienceCard[],
  onMarkerClick?: (experience: ExtendedExperienceCard) => void,
  signal?: AbortSignal
): Promise<MapMarker[]> {
  if (!experiences || experiences.length === 0) {
    return [];
  }

  // Check if Google Maps is loaded
  if (!isGoogleMapsLoaded()) {
    console.warn('Google Maps not loaded, cannot create markers');
    return [];
  }

  // Check abort signal
  if (signal?.aborted) {
    return [];
  }

  const markers: MapMarker[] = [];

  for (let i = 0; i < experiences.length; i++) {
    // Check abort signal before processing each experience
    if (signal?.aborted) {
      break;
    }

    const experience = experiences[i];

    // Use coordinates directly from experience (already extracted by mapper)
    // ExtendedExperienceCard should have latitude/longitude from mapChatExperienceToCard
    // If coordinates are not available, skip this experience (do NOT geocode)
    const lat = experience.latitude;
    const lng = experience.longitude;

    // Skip experiences without coordinates - do not geocode
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
      console.warn(
        '📍 [experienceMapUtils] ❌ Skipping experience - no coordinates found:',
        {
          experienceId: experience.id,
          experienceTitle: experience.title,
          latitude: lat,
          longitude: lng,
          hasLat: experience.latitude !== undefined,
          hasLng: experience.longitude !== undefined,
          totalExperiences: experiences.length,
          processedSoFar: i + 1,
        }
      );
      continue;
    }

    console.log('📍 [experienceMapUtils] ✅ Experience has coordinates:', {
      experienceId: experience.id,
      experienceTitle: experience.title,
      coordinates: { lat, lng },
      processedSoFar: i + 1,
      totalExperiences: experiences.length,
    });

    // Only create marker if we have valid coordinates
    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
      markers.push({
        id: experience.id || `exp-${Math.random()}`,
        title: experience.title || '',
        latitude: lat,
        longitude: lng,
        address: experience.location,
        image: experience.image,
        rating: experience.rating,
        experienceType: experience.type,
        isExperience: true,
        price: getPriceDisplay(experience),
        onClick: onMarkerClick ? () => onMarkerClick(experience) : undefined,
      } as MapMarker);
    } else {
      console.warn(
        `Skipping marker for experience "${experience.title || experience.id}" - no valid coordinates`
      );
    }
  }

  console.log(
    `🗺️ Converted ${markers.length} of ${experiences.length} experiences to markers`
  );

  return markers;
}

/**
 * Get price display text for marker with currency conversion
 */
export function getPriceDisplay(experience: ExtendedExperienceCard): string {
  if (!experience.price) {
    return '';
  }

  // Get currency store state (can be accessed outside React components)
  const {
    currencySymbol,
    currencyCode,
    exchangeRates,
    baseCurrency,
    targetCurrencyRate,
  } = useCurrencyStore.getState();

  // Get numeric price from string or number
  const getNumericPrice = (price: string | number | undefined): number => {
    if (price === undefined || price === null) return 0;

    // If already a number, return it
    if (typeof price === 'number') {
      return isNaN(price) ? 0 : price;
    }

    // If string, remove currency symbols and parse
    const cleaned = price.replace(/[₹$,]/g, '').trim();
    const numValue = parseFloat(cleaned);
    return isNaN(numValue) ? 0 : numValue;
  };

  // Infer backend currency from currencySymbol
  const getBackendCurrency = (symbol?: string | null): string => {
    if (!symbol) return 'USD';
    if (symbol === '₹' || symbol === 'INR') return 'INR';
    if (symbol === '$' || symbol === 'USD') return 'USD';
    // Default to USD for unknown symbols
    return 'USD';
  };

  const numericPrice = getNumericPrice(experience.price);
  if (numericPrice === 0) {
    return '';
  }

  const backendCurrency = getBackendCurrency(experience.currencySymbol);

  // Convert price using currency conversion
  const convertedAmount = convertAmountWithRates({
    amount: numericPrice,
    fromCurrency: backendCurrency,
    toCurrency: currencyCode,
    exchangeRates: exchangeRates || undefined,
    baseCurrency: baseCurrency || 'USD',
  });

  // Fallback to simple rate conversion if exchangeRates don't work
  let finalAmount = convertedAmount;
  if (
    backendCurrency !== currencyCode &&
    (convertedAmount === numericPrice || isNaN(convertedAmount))
  ) {
    // Use targetCurrencyRate as fallback
    if (targetCurrencyRate && targetCurrencyRate > 0) {
      if (backendCurrency === (baseCurrency || 'USD')) {
        finalAmount = numericPrice * targetCurrencyRate;
      } else if (currencyCode === (baseCurrency || 'USD')) {
        finalAmount = numericPrice / targetCurrencyRate;
      } else {
        finalAmount = numericPrice;
      }
    } else {
      finalAmount = numericPrice;
    }
  }

  // Format the amount with Indian number format (commas)
  // Show decimals only if needed, otherwise round to whole number
  const hasDecimals = finalAmount % 1 !== 0;

  const formattedAmount = hasDecimals
    ? finalAmount.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : Math.round(finalAmount).toLocaleString('en-IN');

  return `${currencySymbol || '₹'}${formattedAmount}`;
}
